/**
 * Backend capability flags — ONE mechanism for every block that switched from a mock or a
 * client-side fallback to the API during the 2026-11 release.
 *
 * A flag is a comma-separated list of capability ids in a `VITE_*_API` variable (`"all"` turns
 * every id of the block on). Dev / preview builds also accept a runtime override in
 * `localStorage` so QA can flip one capability without a rebuild:
 *   localStorage.setItem('<storage key>', 'wardrobe_facets,link_item')  → reload
 *
 * Blocks: `features/style-space/flags.ts` (`VITE_STYLE_SPACE_V2_API`), `features/profile/flags.ts`
 * (`VITE_PROFILE_API`), `features/survey/flags.ts` (`VITE_SURVEYS_API`). `.env.example` lists them;
 * `.env.development` turns them all on.
 */

/** The ids named in `raw` that `all` knows; `"all"` → every id. Unknown ids are ignored. */
export function parseCapabilityList<T extends string>(
  raw: string | undefined,
  all: readonly T[],
): ReadonlySet<T> {
  const tokens = (raw ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
  if (tokens.includes('all')) return new Set(all)
  return new Set(tokens.filter((token): token is T => (all as readonly string[]).includes(token)))
}

/** The dev-only `localStorage` override, or `undefined` outside a dev build / without one. */
export function readCapabilityOverride(storageKey: string): string | undefined {
  if (!import.meta.env.DEV || typeof window === 'undefined') return undefined
  try {
    return window.localStorage.getItem(storageKey) ?? undefined
  } catch {
    return undefined
  }
}

/** `parseCapabilityList` of the override when there is one, else of the env variable. */
export function resolveCapabilities<T extends string>(
  envValue: string | undefined,
  storageKey: string,
  all: readonly T[],
): ReadonlySet<T> {
  return parseCapabilityList(readCapabilityOverride(storageKey) ?? envValue, all)
}
